import clsx from "clsx";

import {
  ALL_SEARCH_FIELDS,
  DEFAULT_SEARCH_FIELDS,
  PriceEstimateSearchField,
  SEARCH_FIELD_LABELS,
} from "../../models/Category/InitialPriceEstimate";

interface SearchFieldTogglesProps {
  selected: PriceEstimateSearchField[];
  onChange: (fields: PriceEstimateSearchField[]) => void;
}

/**
 * Clickable vehicle fields for the eBay search term. Picking a field adds it to the end of the term,
 * so the order they are picked in is the word order; the number on each chip shows its place.
 */
export default function SearchFieldToggles({
  selected,
  onChange,
}: SearchFieldTogglesProps) {
  const toggle = (field: PriceEstimateSearchField) =>
    onChange(
      selected.includes(field)
        ? selected.filter((f) => f !== field)
        : [...selected, field],
    );

  const move = (field: PriceEstimateSearchField, by: -1 | 1) => {
    const index = selected.indexOf(field);
    const target = index + by;
    if (index < 0 || target < 0 || target >= selected.length) return;

    const next = [...selected];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  return (
    <section className="bg-white rounded shadow-sm border border-gray-200 p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="text-xs font-semibold tracking-wide text-gray-500 uppercase">
          Search Keywords
        </h2>
        <button
          type="button"
          className="text-xs text-orange-700 hover:underline"
          onClick={() => onChange([...DEFAULT_SEARCH_FIELDS])}
        >
          Reset to Year, Make, Model
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        {ALL_SEARCH_FIELDS.map((field) => {
          const position = selected.indexOf(field);
          const isSelected = position >= 0;

          return (
            <button
              key={field}
              type="button"
              aria-pressed={isSelected}
              onClick={() => toggle(field)}
              className={clsx(
                "flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm",
                isSelected
                  ? "border-orange-600 bg-orange-600/10 text-orange-700 font-medium"
                  : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50",
              )}
            >
              {isSelected && (
                <span className="flex h-4 w-4 items-center justify-center rounded-full bg-orange-600 text-[10px] text-white">
                  {position + 1}
                </span>
              )}
              {SEARCH_FIELD_LABELS[field]}
            </button>
          );
        })}
      </div>

      {selected.length === 0 ? (
        <p className="mt-3 text-xs text-red-700">
          Pick at least one field. A search on the keyword alone would price
          every vehicle the same.
        </p>
      ) : (
        <>
          <p className="mt-4 mb-1 text-xs font-medium text-gray-600">
            Word order
          </p>
          <ol className="divide-y divide-gray-100 rounded border border-gray-200">
            {selected.map((field, index) => (
              <li
                key={field}
                className="flex items-center justify-between px-3 py-1.5 text-sm"
              >
                <span>
                  <span className="mr-2 text-gray-400">{index + 1}.</span>
                  {SEARCH_FIELD_LABELS[field]}
                </span>
                <span className="flex gap-1">
                  <button
                    type="button"
                    aria-label={`Move ${SEARCH_FIELD_LABELS[field]} earlier`}
                    disabled={index === 0}
                    onClick={() => move(field, -1)}
                    className="rounded px-1.5 text-gray-500 hover:bg-gray-100 disabled:opacity-30"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    aria-label={`Move ${SEARCH_FIELD_LABELS[field]} later`}
                    disabled={index === selected.length - 1}
                    onClick={() => move(field, 1)}
                    className="rounded px-1.5 text-gray-500 hover:bg-gray-100 disabled:opacity-30"
                  >
                    ↓
                  </button>
                </span>
              </li>
            ))}
          </ol>
        </>
      )}

      <p className="mt-2 text-xs text-gray-500">
        The selected fields are taken from the lead's vehicle in this order,
        followed by the search keyword. Blank fields are skipped.
      </p>
    </section>
  );
}
